'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';
import CustomerDashboardLayout from '../components/CustomerDashboardLayout';

interface Review {
  id: string;
  product_id: string;
  order_id: string | null;
  rating: number;
  review_text: string | null;
  is_verified_purchase: boolean;
  created_at: string;
}

const STAR_LABELS = ['', 'Terrible', 'Bad', 'Okay', 'Good', 'Excellent'];

export default function CustomerReviewsPage() {
  const router = useRouter();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ product_id: '', rating: 5, review_text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchReviews = useCallback(async (uid: string) => {
    const { data } = await supabase
      .from('product_reviews')
      .select('id, product_id, order_id, rating, review_text, is_verified_purchase, created_at')
      .eq('user_id', uid)
      .order('created_at', { ascending: false });
    setReviews(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { router.push('/customer-login'); return; }
      setUserId(user.id);
      fetchReviews(user.id);
    });
  }, [router, fetchReviews]);

  const handleSubmit = async () => {
    if (!form.product_id) {
      showToast('Please enter a product ID.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      if (editId) {
        const { error } = await supabase
          .from('product_reviews')
          .update({ rating: form.rating, review_text: form.review_text || null })
          .eq('id', editId);
        if (error) throw error;
        showToast('Review updated!', 'success');
      } else {
        const { error } = await supabase.from('product_reviews').insert({
          user_id: userId,
          product_id: form.product_id,
          rating: form.rating,
          review_text: form.review_text || null,
        });
        if (error) throw error;
        showToast('Review submitted!', 'success');
      }
      setShowForm(false);
      setEditId(null);
      setForm({ product_id: '', rating: 5, review_text: '' });
      fetchReviews(userId);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to submit review.';
      showToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (review: Review) => {
    setForm({ product_id: review.product_id, rating: review.rating, review_text: review.review_text || '' });
    setEditId(review.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('product_reviews').delete().eq('id', id);
    if (!error) { showToast('Review deleted.', 'success'); fetchReviews(userId); }
    else showToast('Failed to delete review.', 'error');
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : '—';

  return (
    <CustomerDashboardLayout>
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl text-sm font-500 shadow-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'ExclamationTriangleIcon'} size={16} />
          {toast.msg}
        </div>
      )}

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-700 text-slate-900">My Reviews</h1>
            <p className="text-sm text-slate-500 mt-0.5">Reviews you&apos;ve written for products</p>
          </div>
          <button
            onClick={() => { setShowForm(true); setEditId(null); setForm({ product_id: '', rating: 5, review_text: '' }); }}
            className="btn-primary px-4 py-2.5 rounded-xl text-sm font-600 flex items-center gap-2"
          >
            <Icon name="PlusIcon" size={16} /> Write Review
          </button>
        </div>

        {/* Summary */}
        {reviews.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center mb-3">
                <Icon name="StarIcon" size={18} className="text-amber-500" />
              </div>
              <p className="text-xl font-700 text-slate-900">{avgRating}</p>
              <p className="text-xs text-slate-500 mt-0.5">Average Rating</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
                <Icon name="ChatBubbleBottomCenterTextIcon" size={18} className="text-blue-500" />
              </div>
              <p className="text-xl font-700 text-slate-900">{reviews.length}</p>
              <p className="text-xs text-slate-500 mt-0.5">Total Reviews</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="w-9 h-9 bg-green-50 rounded-lg flex items-center justify-center mb-3">
                <Icon name="ShieldCheckIcon" size={18} className="text-green-500" />
              </div>
              <p className="text-xl font-700 text-slate-900">{reviews.filter(r => r.is_verified_purchase).length}</p>
              <p className="text-xs text-slate-500 mt-0.5">Verified Purchases</p>
            </div>
          </div>
        )}

        {/* Write/Edit Review Form */}
        {showForm && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h2 className="text-base font-700 text-slate-900">{editId ? 'Edit Review' : 'Write a Review'}</h2>
            {!editId && (
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Product ID <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.product_id}
                  onChange={e => setForm(f => ({ ...f, product_id: e.target.value }))}
                  placeholder="Enter the product ID from your order"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-600 text-slate-500 mb-2">Rating <span className="text-red-500">*</span></label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, rating: star }))}
                    className={`text-2xl transition-transform hover:scale-110 ${star <= form.rating ? 'text-amber-400' : 'text-slate-200'}`}
                  >
                    ★
                  </button>
                ))}
                <span className="text-sm text-slate-500 ml-2">{STAR_LABELS[form.rating]}</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-600 text-slate-500 mb-1.5">Review</label>
              <textarea
                value={form.review_text}
                onChange={e => setForm(f => ({ ...f, review_text: e.target.value }))}
                rows={4}
                placeholder="Share your experience with this product…"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-primary px-6 py-2.5 rounded-xl text-sm font-600 disabled:opacity-60 flex items-center gap-2"
              >
                {submitting ? <><Icon name="ArrowPathIcon" size={14} className="animate-spin" /> Saving…</> : (editId ? 'Update Review' : 'Submit Review')}
              </button>
              <button
                onClick={() => { setShowForm(false); setEditId(null); }}
                className="px-6 py-2.5 rounded-xl text-sm font-600 border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Reviews list */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
              <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Icon name="StarIcon" size={24} className="text-slate-300" />
              </div>
              <p className="text-sm text-slate-400">No reviews yet</p>
              <p className="text-xs text-slate-400 mt-1">Share your experience by writing a review</p>
            </div>
          ) : (
            reviews.map(review => (
              <div key={review.id} className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map(star => (
                          <span key={star} className={`text-base ${star <= review.rating ? 'text-amber-400' : 'text-slate-200'}`}>★</span>
                        ))}
                      </div>
                      <span className="text-xs font-600 text-slate-600">{STAR_LABELS[review.rating]}</span>
                      {review.is_verified_purchase && (
                        <span className="text-xs bg-green-100 text-green-700 font-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Icon name="ShieldCheckIcon" size={10} /> Verified Purchase
                        </span>
                      )}
                    </div>
                    {review.review_text && (
                      <p className="text-sm text-slate-700 leading-relaxed">{review.review_text}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-2">
                      {new Date(review.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button onClick={() => handleEdit(review)} className="text-xs text-primary font-600 hover:underline">Edit</button>
                    <button onClick={() => handleDelete(review.id)} className="text-xs text-red-500 hover:underline font-500">Delete</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </CustomerDashboardLayout>
  );
}
